
<template>
  <div class="w-full max-w-xs sm:max-w-md mx-auto py-2 px-4 rounded-lg">
    <div class="bg-green-500 text-white px-4 py-2 rounded-t-md text-center font-bold text-xl">
      Get a Free Property Cash Offer
    </div>
    <div class="bg-green-500 text-white px-4 py-1 text-center font-semibold text-sm">
      No-Obligation - Instant Decision<br>Get a Quick Quote Now
    </div>
    <form @submit.prevent="handleSubmit" class="bg-white shadow-md rounded-b-md px-6 sm:px-8 pt-6 pb-4">
      <div class="mb-4">
        <label class="block text-gray-700 font-bold mb-2 text-lg" for="postcode">
          Get Started...
        </label>
        <!-- Postcode Input -->
        <div class="flex">
          <input 
            v-model="postcode" 
            @input="handlePostcodeInput" 
            class="mobile-input rounded-full bg-gray-200 border-2 w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:border-blue-500 text-lg"
            :class="postcodeValidated ? 'border-green-500 bg-green-50' : 'border-blue-300'"
            id="postcode" 
            type="text" 
            placeholder="Enter your Postcode..."
            :disabled="postcodeValidated"
          />
          <button 
            v-if="!postcodeValidated"
            class="submit-btn bg-green-500 hover:bg-green-600 text-white px-6 rounded-full font-bold transition duration-300 ease-in-out transform hover:scale-105" 
            type="button"
            @click="validatePostcode"
          >GO</button>
          <button 
            v-else
            class="submit-btn bg-gray-400 hover:bg-gray-500 text-white px-4 rounded-full font-bold transition duration-300 ease-in-out"
            type="button"
            @click="resetPostcode"
          >Edit</button>
        </div>
        
        <!-- Postcode validation message -->
        <div v-if="postcodeError" class="text-red-500 text-sm mt-2">{{ postcodeError }}</div>
        <div v-if="postcodeValidated" class="text-green-600 text-sm mt-2">✓ Postcode validated</div>

        <!-- Address Input (shows after postcode validated) -->
        <div v-if="postcodeValidated" class="mt-4">
          <label class="block text-gray-700 font-bold mb-2 text-base" for="streetAddress">
            Enter your property address:
          </label>
          <input 
            v-model="streetAddress" 
            class="rounded-lg bg-gray-200 border-2 border-blue-300 w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:border-blue-500 text-base"
            id="streetAddress" 
            type="text" 
            placeholder="e.g. 123 Main Street, Apartment 4"
            ref="streetAddressInput"
          />
          <p class="text-gray-500 text-xs mt-1">Enter your house number and street name</p>
          
          <!-- Continue Button -->
          <button 
            v-if="streetAddress.trim().length >= 3"
            class="mt-4 w-full bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-full font-bold text-lg transition duration-300 ease-in-out transform hover:scale-105" 
            type="submit"
          >
            Continue →
          </button>
        </div>

        <div v-if="submitted && !postcode" class="text-red-500 text-lg mt-2">Please enter a postcode</div>
      </div>
      <div class="review-section border-t-2 border-gray-200 mt-6 pt-6">
        <div class="flex flex-col items-center justify-center">
          <p class="review-text text-gray-600 italic text-center text-sm sm:text-lg">{{ reviewText }}</p>
          <div class="star-rating-logo-wrapper flex flex-col items-center justify-center mt-0">
            <div class="star-rating text-yellow-400 text-2xl">
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
            </div>
            <img :src="require('@/assets/reviewsio-logo.png')" alt="Reviews.io Logo" class="h-5 mt-0" />
          </div>
          <p class="reviewer-info text-gray-500 text-lg mt-2 text-center">{{ reviewerName }}</p>
        </div>
      </div>
      <div class="trust-logos mt-4">
        <img src="@/assets/trust-logos.svg" alt="Trust Logos" class="mx-auto" style="width: 80%; max-width: 300px; height: auto;">
      </div>
    </form>
  </div>
</template>

<script>
export default {
  data() {
    return {
      postcode: "",
      streetAddress: "",
      postcodeValidated: false,
      postcodeError: "",
      submitted: false,
    };
  },
  methods: {
    handlePostcodeInput() {
      // Clear error when user types
      this.postcodeError = "";
    },
    
    isValidPostcode(postcode) {
      // UK postcode regex validation
      const regex = /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))\s?[0-9][A-Za-z]{2})$/;
      return regex.test(postcode.trim());
    },
    
    validatePostcode() {
      this.submitted = true;
      this.postcodeError = "";
      
      if (!this.postcode.trim()) {
        this.postcodeError = "Please enter a postcode";
        return;
      }
      
      if (!this.isValidPostcode(this.postcode)) {
        this.postcodeError = "Please enter a valid UK postcode";
        return;
      }
      
      // Format the postcode nicely (uppercase)
      this.postcode = this.postcode.toUpperCase().trim();
      this.postcodeValidated = true;
      
      // Focus on the address input after validation
      this.$nextTick(() => {
        if (this.$refs.streetAddressInput) {
          this.$refs.streetAddressInput.focus();
        }
      });
    },
    
    resetPostcode() {
      this.postcodeValidated = false;
      this.streetAddress = "";
      this.postcodeError = "";
    },
    
    handleSubmit() {
      if (!this.postcodeValidated) {
        this.validatePostcode();
        return;
      }
      
      if (!this.streetAddress || this.streetAddress.trim().length < 3) {
        return;
      }
      
      // Emit the address data
      const fullAddress = `${this.streetAddress.trim()}, ${this.postcode}`;
      this.$emit("addressSelected", { 
        street: fullAddress, 
        postcode: this.postcode, 
        country: "United Kingdom" 
      });
      
      this.proceedToQuiz();
    },
    
    proceedToQuiz() {
      this.$emit('proceedToQuiz');
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    },
  },
  computed: {
    showSuggestions() {
      return this.addressSuggestions.length > 0 && this.suggestionsVisible;
    },
    reviewText() {
      if (this.$route.path.startsWith('/financial/')) {
        return "World was about to end, and lose my home, the fight in me made me not bury my head and made the call I had 48hrs till eviction and Adam was so calm. Made the whole process without stress and was a man of his word from start to end five-star people don’t lose your home make the call";
      } else {
        return "The offer that was given did not change... I Was getting extremely frustrated going with companies that would give me a high sale price then drop it at the last minute. Luckily, I found Adam and rapid Sale who was honest from the start. The offer that was given did not change. All got complete very quickly with no hassle. If your after a quick sale be careful of a lot of companies that pop up first when searching and give Adam a call. Would highly recommend Adam and his team.";
      }
    },
    reviewerName() {
      if (this.$route.path.startsWith('/financial/')) {
        return "- Neil Whitehall";
      } else {
        return "By Mark Lynas";
      }
    }
  }
};
</script>




<style scoped>
/* Base styles for the form container */
.w-full {
  max-width: 100%;
}

/* Padding for better spacing on all devices */
.py-6 {
  padding: 2rem 1rem;
}

/* Form header styles */
.bg-green-500 {
  background-color: #38a169; /* Example green color */
}

.text-white {
  color: white;
}

/* Postcode input and button styles */
.mobile-input,
.submit-btn {
  padding: 0.5rem 1rem;
  font-size: 1rem;
  border-radius: 9999px; /* Full roundness */
}

/* Button-specific styles */
.submit-btn {
  background-color: #48bb78; /* Example green color for the button */
  color: white;
  margin-left: 0.5rem; /* Space between the input and button */
}

/* Review section styles */
.review-section {
  background-color: #f7f7f7;
  border-radius: 10px;
  padding: 2rem; /* Increased padding for larger quotes */
  margin-top: 2rem;
  border-top: 2px solid #e2e8f0;
  position: relative;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Pseudo-elements for quotes */
.review-section:before,
.review-section:after {
  color: #cbd5e0; /* Light color for the quote icon */
  position: absolute;
  font-family: "Times New Roman", Times, serif;
  z-index: 0;
}

.review-section:before {
  content: '“';
  top: 0;
  left: 0.5rem;
  font-size: 4em;
  line-height: 1;
}

.review-section:after {
  content: '”';
  bottom: 0;
  right: 0.5rem;
  font-size: 4em;
  line-height: 1;
}

/* Text styles for review and rating */
.review-text {
  font-style: italic;
  color: #4a5568; /* Darker color for contrast */
  line-height: 1.6; /* Adjust line height for better readability */
  max-width: 925px; /* Maximum width of the review text block */
  margin: auto; /* Center the text block */
}

/* Star rating styles */
.star-rating {
  font-size: 1.25rem;
  color: #ecc94b; /* Gold color for stars */
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
  margin-top: 1rem; /* Add space above the star rating */
}

.reviewer-info {
  font-size: 0.9rem; /* Smaller font size for meta information */
  color: #718096; /* Muted color for less emphasis */
  text-align: right; /* Align to the right */
  position: relative;
  z-index: 1;
}

/* Media query for mobile devices */
@media only screen and (max-width: 600px) {
  /* Adjust the main container padding */
  .w-full.max-w-xs.sm\:max-w-md.mx-auto.py-2.px-4.rounded-lg {
    padding: 1rem; /* Consistent padding on mobile */
  }
  .review-text {
    /* Adjust maximum width for smaller screens */
    max-width: 100%;
  }

  /* Adjust the form's overall padding to be consistent */
  .bg-white.shadow-md.rounded-b-md.px-6.sm\:px-8.pt-6.pb-4 {
    padding: 1rem; /* Adds padding inside the form */
  }

  /* Styles for input and button to ensure they do not touch the container edges */
  .mobile-input {
    flex-grow: 1; /* Input will grow to take available space */
    max-width: calc(100% - 60px); /* Adjust based on button width */
  }

  .submit-btn {
    flex-shrink: 0; /* Button will not shrink */
    min-width: 50px; /* Set a minimum width for the button */
  }

  /* Adjust the review text size for better readability */
  .review-text,
  .reviewer-info,
  .star-rating i {
    font-size: 0.775rem;
  }
}
</style>

